import "./themes-dashboard.scss";

import React from "react";
import {cx} from "emotion";

import {consumeContext} from "@common/react/context";
import {Load2} from "@common/react/load2";
import {cs} from "@common/react/chain-services";
import {UseState} from "@common/react/use-state";
import {FolderCM} from "../collections/folder-cm/folder-cm";
import {MoveToDialog} from "../common/move-to-dialog/move-to-dialog";
import {PopupMenu} from "../../../common/popup-menu/popup-menu";
import {RecursiveTable} from "../../../common/recursive-table/recursive-table";
import {buildFoldersTree} from "../collections/folders-tree-structure";
import {LoadingIndicator} from "@common/ui-components/loading-indicator/loading-indicator";
import {createDateFormatter} from "@common/ui-components/charts/common/formatters/date-formatter";
import {CreateFolderDialog} from "../collections/folder-cm/create-folder/create-folder-dialog";
import {DeleteThemeDialog} from "../../../collection/theme/edit/delete-theme-dialog/delete-theme-dialog";
import {flatten1} from "@common/utils/collections";
import {CreateThemeDialog} from "./create-theme-dialog";
import {isBlank} from "@common/utils/strings";
import {SearchInputBar} from "@common/ui-components/search-input-bar/search-input-bar";
import {ButtonDropdown} from "../../../data-source/common/button-dropdown/button-dropdown";
import {ButtonIconWithTooltip} from "@common/form/buttons/button-icon/button-icon";
import {DocsIcon, NewFolderIcon, PlusIcon} from "@common/ui-components/icons/global-icons";
import {cSsJson} from "@common/react/ss-json";
import {MultiSelect} from "../../common/multi-select";
import {noun} from "@common/utils/plural";

export const ThemesDashboard = ({next, updateEvents, environment}) =>
    cs(
        consumeContext("apis"),
        consumeContext("routing"),
        consumeContext("auth"),
        ({apis}, next) => (apis.model == null ? "Loading..." : next()),
        [
            "folders",
            ({apis, routing}, next) =>
                Load2({
                    _key: routing.params?.envId,
                    fetch: () => apis.theme.getFolders(),
                    next,
                }),
        ],
        // remove bad folders
        // ({folders, apis}) => {
        //     console.log(folders.value);
        //     return folders.value && Invoke({
        //         fn: async () => {
        //             const needDeleteFolders = folders.value.filter(t => t.themes.length == 0);
        //
        //             console.log(needDeleteFolders);
        //
        //             await Promise.all(needDeleteFolders.map(f => apis.theme.deleteFolder({folderId: f.id, confirmName: f.name})))
        //
        //
        //             // const needDeleteFolders = folders.value.filter(t => t.parentFolderID);
        //             //
        //             // console.log(needDeleteFolders);
        //             //
        //             // await Promise.all(needDeleteFolders.map(f => apis.theme.upsertFolder({...f, parentFolderID: null})))
        //         }
        //     })
        // },
        [
            "onUpdateFolders",
            ({folders}, next) =>
                next((newFolders) => {
                    const res = folders.onChange(newFolders);
                    updateEvents.emit("reload");
                    return res;
                }),
        ],
        [
            "moveThemeDialog",
            ({onUpdateFolders, folders}, next) =>
                MoveToDialog({
                    apiType: "theme",
                    folders: folders.value,
                    onDone: onUpdateFolders,
                    next,
                }),
        ],
        [
            "deleteThemeDialog",
            ({onUpdateFolders, folders}, next) =>
                DeleteThemeDialog({
                    themes: flatten1((folders.value || []).map((f) => f.themes)),
                    onDone: onUpdateFolders,
                    reloadFolderOnDone: true,
                    next,
                }),
        ],
        [
            "createFolderDialog",
            ({folders}, next) =>
                CreateFolderDialog({
                    apiType: "theme",
                    folders: folders.value,
                    onCreate: (newFolder) => folders.onChange([...(folders.value || []), newFolder]),
                    next,
                }),
        ],
        ["createThemeService", (_, next) => CreateThemeDialog({next})],
        [
            "multiSelectCtrl",
            ({moveThemeDialog, deleteThemeDialog}, next) =>
                MultiSelect({
                    next,
                    itemName: "Theme",
                    resolveItemKey: (item) => `${item.type}_${item.id}`,
                    actions: [
                        {
                            label: "Move To",
                            isShow: () => true,
                            onClick: async (selectedItems) => {
                                const updateItems = Object.values(selectedItems);
                                return await moveThemeDialog.show({
                                    isUpdateMany: true,
                                    label: `${updateItems.length} ${noun(updateItems.length, "Source")}`,
                                    params: updateItems,
                                });
                            },
                        },
                        {
                            label: "Delete",
                            isShow: () => true,
                            onClick: async (selectedItems) => {
                                const updateItems = Object.values(selectedItems);
                                return await deleteThemeDialog.show({
                                    themes: updateItems,
                                });
                            },
                        },
                    ],
                }),
        ],
        [
            "keyword",
            (_, next) =>
                UseState({
                    initValue: cSsJson("SEARCH_VALUE").get()?.themes || "",
                    next,
                }),
        ],
        ({routing, keyword, folders, createFolderDialog, multiSelectCtrl, createThemeService, deleteThemeDialog, moveThemeDialog}) => {
            return next({
                actions: (
                    <div className="models-dashboard-actions-2rf">
                        {SearchInputBar({state: keyword, type: "themes"})}

                        {!environment?.readOnly && (
                            <>
                                {ButtonDropdown({
                                    label: "New",
                                    list: [
                                        {
                                            value: 0,
                                            label: (
                                                <>
                                                    <PlusIcon fill="#919EB0" /> Add New Theme
                                                </>
                                            ),
                                        },
                                        {
                                            value: 1,
                                            label: (
                                                <>
                                                    <NewFolderIcon fill="#919EB0" /> Create New Folder
                                                </>
                                            ),
                                        },
                                    ],
                                    valueToLabel: (item) => item.label,
                                    onChange: (item) => {
                                        const maps = {
                                            0: async () => {
                                                const themeID = await createThemeService.show();
                                                if (themeID) {
                                                    routing.goto("edit-theme", {
                                                        themeId: themeID,
                                                    });
                                                }
                                            },
                                            1: createFolderDialog.show,
                                        };

                                        return maps[item.value]();
                                    },
                                })}
                            </>
                        )}
                    </div>
                ),
                content: cs(
                    [
                        "folderCM",
                        (_, next) =>
                            FolderCM({
                                apiType: "theme",
                                folders,
                                next,
                                updateEvents,
                            }),
                    ],
                    [
                        "themeCM",
                        ({}, next) =>
                            PopupMenu({
                                getCommands: ({theme}) => {
                                    return [
                                        {
                                            label: "Edit",
                                            onClick: () =>
                                                routing.goto("edit-theme", {
                                                    themeId: theme.id,
                                                }),
                                        },
                                        ...(!environment?.readOnly
                                            ? [
                                                  {
                                                      label: "Move to",
                                                      onClick: () =>
                                                          moveThemeDialog.show({
                                                              params: theme,
                                                          }),
                                                  },
                                                  {
                                                      label: "Delete",
                                                      className: "text-danger",
                                                      onClick: () => deleteThemeDialog.show({themes: [theme]}),
                                                  },
                                              ]
                                            : []),
                                    ];
                                },
                                next,
                            }),
                    ],
                    ({themeCM, folderCM}) => {
                        const hasSelectedItems = multiSelectCtrl.numSelectedItems > 0;
                        const tableHeight = `calc(100vh - ${78 + 54 + 40 + (hasSelectedItems ? 52 : 0)}px)`;
                        const isFolder = (item) => item?.hasOwnProperty("children") || item?.hasOwnProperty("themes");
                        return (
                            <div className="collections-dashboard-content-98y">
                                {hasSelectedItems && multiSelectCtrl.render()}

                                {!folders.value
                                    ? LoadingIndicator()
                                    : RecursiveTable({
                                          // className: "models-table",
                                          label: "Themes",
                                          showNoResult: keyword.value?.length > 0,
                                          onClearSearch: () => keyword.onChange(""),
                                          maxHeight: tableHeight,
                                          getTrClass: (n) => multiSelectCtrl.isSelected(n) && "is-selected-tr",
                                          getChildTrClass: (n) => (isFolder(n) && multiSelectCtrl.isSelected(n) ? "is-selected-tr" : ""),
                                          structure: buildFoldersTree({
                                              itemsProp: "themes",
                                              folders: folders.value,
                                              keyword: keyword.value,
                                          }),
                                          isGroupNode: (n) => n?.themes,
                                          isAlwaysExpanded: () => !isBlank(keyword.value),
                                          onClickRow: (item, event) => {
                                              if (isFolder(item)) {
                                                  return;
                                              }
                                              event.preventDefault();
                                              event.stopPropagation();
                                              multiSelectCtrl.toggleSelect(item);
                                          },
                                          columns: [
                                              {
                                                  label: "Name",
                                                  indented: true,
                                                  format: (m, {expanding}) => {
                                                      if (!m.versionInfo) {
                                                          return (
                                                              <div
                                                                  className={cx("icon-name-34n", "folder", {
                                                                      expanding,
                                                                  })}
                                                              >
                                                                  <img
                                                                      src={
                                                                          expanding
                                                                              ? require("../collections/icons/folder-open-icon.svg")
                                                                              : require("../collections/icons/folder-icon.svg")
                                                                      }
                                                                      alt={""}
                                                                  />
                                                                  {m.name}
                                                              </div>
                                                          );
                                                      }

                                                      return (
                                                          <div
                                                              className="model-name-89e theme-col"
                                                              onClick={(e) => {
                                                                  e.preventDefault();
                                                                  e.stopPropagation();
                                                                  routing.goto("edit-theme", {
                                                                      themeId: m.id,
                                                                  });
                                                              }}
                                                          >
                                                              {m.name}

                                                              {m.default && <div className="default">Default</div>}
                                                          </div>
                                                      );
                                                  },
                                              },
                                              {
                                                  label: "Number of Collections",
                                                  format: (m) => m.numCollections,
                                              },
                                              {
                                                  label: "Last modified by",
                                                  format: (m) => m.versionInfo && m.versionInfo?.editedBy,
                                              },
                                              {
                                                  label: "Last modified on",
                                                  format: (m) => {
                                                      const editedOn = new Date(m.versionInfo?.editedOnUtc);
                                                      return (
                                                          m.versionInfo &&
                                                          `${createDateFormatter("MMM d, yyyy @ h:mm tt").format(editedOn)}`
                                                      );
                                                  },
                                              },
                                              {
                                                  label: "Published on",
                                                  format: (c) => {
                                                      if (!c.versionInfo) return "";

                                                      const publishedOn = new Date(c.publishedOnUtc || new Date().toUTCString());
                                                      return (
                                                          <div
                                                              className={cx("publishing-date-23x", {
                                                                  pending: c.publishedOnUtc === null,
                                                              })}
                                                          >
                                                              <img
                                                                  src={
                                                                      c.publishedOnUtc
                                                                          ? require("../../../common/icons/published-icon.svg")
                                                                          : require("../../../common/icons/not-published-icon.svg")
                                                                  }
                                                                  alt=""
                                                              />
                                                              {c.publishedOnUtc
                                                                  ? createDateFormatter("MMM d, yyyy @ h:mm tt").format(publishedOn)
                                                                  : "Not Published"}
                                                          </div>
                                                      );
                                                  },
                                              },
                                              {
                                                  format: (m) => (
                                                      <div className="cm">
                                                          {!m.versionInfo
                                                              ? folderCM.render({
                                                                    params: m,
                                                                })
                                                              : themeCM.render({
                                                                    params: {
                                                                        theme: m,
                                                                    },
                                                                })}
                                                      </div>
                                                  ),
                                                  className: "models-cm-35v",
                                                  shy: true,
                                                  alignRight: true,
                                              },
                                          ],
                                      })}
                            </div>
                        );
                    }
                ),
            });
        }
    );
